import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../firebase/config';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps the app and makes auth object available to any child component that calls useAuth()
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState(null);

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      setError('');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get the ID token and store it in localStorage
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('authToken', token);

      return userCredential.user;
    } catch (error) {
      setError('Failed to sign in: ' + error.message);
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError('');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Get the ID token and store it in localStorage
      const token = await result.user.getIdToken();
      localStorage.setItem('authToken', token);

      return result.user;
    } catch (error) {
      setError('Failed to sign in with Google: ' + error.message);
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError('');
      await firebaseSignOut(auth);

      // Clear the token from localStorage
      localStorage.removeItem('authToken');
    } catch (error) {
      setError('Failed to sign out: ' + error.message);
      throw error;
    }
  };

  // Check if user is admin.
  //
  // Admin status is a Firebase custom claim ("roles" contains "admin") — the same
  // source of truth the backend uses to grant ROLE_ADMIN (see FirebaseTokenFilter).
  // We read it directly from the signed ID token rather than from /users/me, whose
  // UserDto has no `admin` field. This is UX-only gating; real enforcement stays on
  // the backend via @PreAuthorize("hasRole('ADMIN')").
  const checkAdminRole = async (user) => {
    if (!user) return false;

    try {
      // Force refresh so we pick up the latest claims (claims can take up to ~1h
      // to propagate after first being assigned server-side).
      const tokenResult = await user.getIdTokenResult(true);

      // Store the token in localStorage for API requests
      localStorage.setItem('authToken', tokenResult.token);

      const rawRoles = tokenResult.claims.roles;
      const roles = Array.isArray(rawRoles) ? rawRoles : (rawRoles ? [rawRoles] : []);
      const isAdmin = roles.includes('admin');

      setUserRole(isAdmin ? 'admin' : 'user');
      return isAdmin;
    } catch (error) {
      console.error('Error checking admin role:', error);
      setUserRole(null);
      return false;
    }
  };

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Get a fresh token and store it in localStorage
        try {
          const token = await user.getIdToken(true);
          localStorage.setItem('authToken', token);
        } catch (error) {
          console.error('Error getting token:', error);
        }

        await checkAdminRole(user);
      } else {
        setUserRole(null);
        localStorage.removeItem('authToken');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    loading,
    error,
    signIn,
    signInWithGoogle,
    signOut,
    checkAdminRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
